/**
 * SBI Import operations service.
 * Provides async functions for importing fields via SBI number.
 */

import { QueryClient } from "@tanstack/react-query";
import { fetchApi } from "common/utils/Api";
import type { ArableField, Field } from "model";
import {
  getAllArableFieldGeomsFromFields,
  getAllFieldGeomsFromFields,
} from "model/fieldHelpers";
import { useAppStore, useUIStore } from "stores";
import { fetchFarmByIdRQ, initializeFarmRQ } from "./initializationRQ";

export type SBIImportPayload = {
  SBINumber: number;
  userGuid: string;
  farmGuid: string;
};

type SBIImportResponse = {
  farmGuid: string;
  fieldGuids: string[];
  fieldsCreated: number;
};

/**
 * Import SBI fields to an existing farm.
 * Service function for SBI import operations.
 */
export async function importSBIToExistingFarm(
  {
    sbiNumber,
    farmGuid,
  }: {
    sbiNumber: number;
    farmGuid: string;
  },
  queryClient: QueryClient
): Promise<SBIImportResponse> {
  const user = useAppStore.getState().user;

  if (!user?.guid) {
    throw new Error("User not authenticated");
  }

  const response = (await fetchApi("SBIImport", {
    method: "POST",
    body: JSON.stringify({
      SBINumber: sbiNumber,
      userGuid: user.guid,
      farmGuid,
    }),
  })) as SBIImportResponse;

  // After import, refresh the farm data to get correct field categorization
  if (response.fieldGuids && response.fieldGuids.length > 0) {
    const currentFarmId = useAppStore.getState().currentFarmId;

    if (currentFarmId) {
      // Fetch the updated farm data (includes new field IDs)
      const updatedFarm = await fetchFarmByIdRQ(currentFarmId);

      // Initialize the farm with the updated data
      await initializeFarmRQ(updatedFarm);
    }

    // Center the map on the newly imported fields - get field geoms from React Query cache
    setTimeout(() => {
      // Get fields from React Query cache
      const queryCache = queryClient.getQueryCache();
      const fieldQueries = queryCache
        .getAll()
        .filter(
          (query) =>
            query.queryKey[0] === "field" &&
            typeof query.queryKey[1] === "string" &&
            query.state.data
        );
      const fields = fieldQueries
        .map((query) => query.state.data as Field)
        .filter(Boolean);

      const arableFieldQueries = queryCache
        .getAll()
        .filter(
          (query) =>
            query.queryKey[0] === "arableField" &&
            typeof query.queryKey[1] === "string" &&
            query.state.data
        );
      const arableFields = arableFieldQueries
        .map((query) => query.state.data as ArableField)
        .filter(Boolean);

      const regularFieldGeoms = getAllFieldGeomsFromFields(fields);
      const arableFieldGeoms = getAllArableFieldGeomsFromFields(arableFields);

      if (regularFieldGeoms.features.length > 0) {
        useUIStore.getState().setZoomToGeom(regularFieldGeoms);
      } else if (arableFieldGeoms.features.length > 0) {
        useUIStore.getState().setZoomToGeom(arableFieldGeoms);
      }
    }, 500);
  }

  return response;
}
